import React, { useState, useEffect } from "react";
import {
  Button,
  Box,
  Grid,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Card,
  FormControlLabel,
  Checkbox,
  FormControl,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  Avatar,
  colors,
} from "@mui/material";
import { FileDownloadOutlined, MoreVert } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import OrderCard from "../../components/cards/OrderCard";
import homeImg from "../../assets/images/homeImg.svg";
import phone from "../../assets/images/svg/phone.svg";
import calendarColor from "../../assets/images/calendar-color.png";
import calendar from "../../assets/images/calendar.png";
import calendarWhite from "../../assets/images/calendar-white.png";
import chat from "../../assets/images/svg/chat2.svg";
import calendarDashboard from "../../assets/images/svg/calendarDashboard.svg";
import DriversBarChart from "../../components/customCharts/DriversBarChart";
import TotalRevenueLineChart from "../../components/customCharts/TotalRevenueLineChart";
import CustomerBarChart from "../../components/customCharts/CustomerBarChart";
import ReviewCarousel from "../../components/carousels/ReviewCarousel";
import { $crud } from "../../../src/utils/CrudFactory";
import EventIcon from "@mui/icons-material/Event"; // calendar icon
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"; // top icon
import PieChart from "../../components/customCharts/PieChart";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "../../components/AdminProvider";

export default function Dashboard({ theme }) {
  const navigate = useNavigate();

  const {
    adminData: { data: user = {} },
  } = useAdmin();
  const [filtrationData, setFiltrationData] = useState({
    from: null,
    to: null,
  });
  const [loading, setLoading] = useState(false);
  const [customerMap, setCustomerMap] = React.useState("week");
  const [stats, setStats] = useState({
    totalOrders: 0,
    deliveredOrders: 0,
    cancelledOrders: 0,
    totalRevenue: 0,
    totalDrivers: 0,
  });

  const [vipStats, setVipStats] = useState({
    totalOrders: 0,
    deliveredOrders: 0,
    cancelledOrders: 0,
    totalRevenue: 0,
    totalDrivers: 0,
  });

  const [data, setData] = useState([]);
  const [overall, setOverall] = useState(0);
  const [days, setDays] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [latestOrders, setLatestOrders] = useState([]);
  const [showAllOrders, setShowAllOrders] = useState(false);
  const [todayOrders, setTodayOrders] = useState([]);
  const [next7DaysOrders, setNext7DaysOrders] = useState([]);
  const [todayOrderCount, setTodayOrderCount] = useState(0);
  const [userCounts, setUserCounts] = useState({});

  const [chartData, setChartData] = useState([]);
  const [selectedYear, setSelectedYear] = useState("2025"); // for highlight
  const [error, setError] = useState(null);

  const fetchRevenueData = async () => {
    setLoading(true);
    setError(null);
    try {
      const years = ["2024", "2025"]; // always fetch both
      const yearsParam = years.join(",");
      const result = await $crud.retrieve(
        `/admin/revenue-trends?years=${yearsParam}`,
      );

      if (result.type === "success") {
        const { monthlyRevenue } = result.data;

        // Combine both years' data into one array
        const combinedData = [];
        for (let i = 0; i < 12; i++) {
          combinedData.push({
            name: monthlyRevenue["2024"][i].month,
            2024: monthlyRevenue["2024"][i].revenue,
            2025: monthlyRevenue["2025"][i].revenue,
          });
        }

        setChartData(combinedData);
      } else {
        setError(result.message || "Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching revenue data:", error);
      setError("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRevenueData();
  }, []);

  const fetchOrderStats = async () => {
    try {
      setLoading(true);
      const params = {};

      params.from =
        !filtrationData.from && !filtrationData.to
          ? new Date(new Date().setHours(0, 0, 0, 0)).toISOString()
          : filtrationData.from
            ? new Date(
                new Date(filtrationData.from).setHours(0, 0, 0, 0),
              ).toISOString()
            : "";
      params.to =
        !filtrationData.from && !filtrationData.to
          ? new Date(new Date().setHours(23, 59, 59, 999)).toISOString()
          : filtrationData.to
            ? new Date(
                new Date(filtrationData.to).setHours(23, 59, 59, 999),
              ).toISOString()
            : "";

      const res = await $crud.retrieve("/admin/order-stats", params);
      const { regular, vip } = res.data || { regular: {}, vip: {} };

      setStats({
        totalOrders: regular.totalOrders || 0,
        deliveredOrders: regular.deliveredOrders || 0,
        cancelledOrders: regular.cancelledOrders || 0,
        totalRevenue: regular.totalRevenue || 0,
        totalDrivers: regular.totalDrivers || 0,
      });
      setVipStats({
        totalOrders: vip.totalOrders || 0,
        deliveredOrders: vip.deliveredOrders || 0,
        cancelledOrders: vip.cancelledOrders || 0,
        totalRevenue: vip.totalRevenue || 0,
        totalDrivers: vip.totalDrivers || 0,
      });
    } catch (err) {
      console.error("Error fetching order stats:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderStats();
  }, [filtrationData.from, filtrationData.to]);

  const handleDateChange = (key, newValue) => {
    const formattedDate = newValue ? newValue : null;
    setFiltrationData((prev) => ({
      ...prev,
      [key]: formattedDate,
    }));
  };

  useEffect(() => {
    const fetchCalendar = async () => {
      try {
        const res = await $crud.retrieve("/admin/calendar");
        const { todayOrders, last7DaysOrders, todayOrderCount } = res.data;

        setTodayOrders(todayOrders);
        setNext7DaysOrders(last7DaysOrders);
        setTodayOrderCount(todayOrderCount);
      } catch (err) {
        console.error("Error fetching calendar orders:", err);
      }
    };

    fetchCalendar();
  }, []);
  useEffect(() => {
    const fetchLatestOrders = async () => {
      try {
        const res = await $crud.retrieve("/admin/latest-orders");
        setLatestOrders(res.data || []);
      } catch (err) {
        console.error("Error fetching latest orders:", err);
      }
    };

    fetchLatestOrders();
  }, []);

  const handleSeeAllOrders = () => {
    navigate("/services/orders");
  };

  const ordersToDisplay = showAllOrders
    ? latestOrders
    : latestOrders.slice(0, 3);

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const res = await $crud.retrieve("/admin/active-drivers");
        setDrivers(res.data || []);
      } catch (err) {
        console.error("Error fetching active drivers:", err);
      }
    };

    fetchDrivers();
  }, []);

  const handleClick = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleSeeAll = () => {
    navigate("/services/drivers");
  };

  // Only show top 3 unless "see all" is clicked
  const displayedDrivers = showAll ? drivers : drivers.slice(0, 3);

  useEffect(() => {
    const fetchWeeklyDrivers = async () => {
      try {
        const res = await $crud.retrieve("admin/weekly-active-drivers");
        if (res.data) {
          setOverall(res.data.overall);
          setDays(res.data.days);
        }
      } catch (err) {
        console.error("Failed to fetch weekly active drivers:", err);
      }
    };
    fetchWeeklyDrivers();
  }, []);

  const handleChange = (event) => {
    setCustomerMap(event.target.value);
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await $crud.retrieve("admin/regular-orders");
        if (res.data) {
          setStats(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch stats:", err);
      }
    };

    fetchStats();
  }, []);

  useEffect(() => {
    const fetchVipStats = async () => {
      try {
        const res = await $crud.retrieve("admin/vip-orders");
        if (res.data) {
          setVipStats(res.data);
        }
      } catch (err) {
        console.error("Failed to fetch VIP order stats:", err);
      }
    };

    fetchVipStats();
  }, []);

  useEffect(() => {
    (async () => {
      $crud
        .retrieve("admin/user-counts")
        .then((data) => {
          setUserCounts(data.data);
        })
        .catch((e) => {
          console.error(e);
        });
    })();
  }, []);

  return (
    <Grid
      container
      sx={{
        padding: "0 18px",
      }}
      spacing={2}
    >
      <Grid item xs={12} md={6}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            fontSize: "24px",
            color: "#673C8E", // Return the value directly
          }}
        >
          Home
        </Typography>
        <Typography sx={{ color: "#A3A3A3", fontSize: "18px" }}>
          Hi, {user?.name}. Welcome back to Vpickup Admin!
        </Typography>
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        display="flex"
        justifyContent="end"
        gap={2}
        alignItems="center"
      >
        <Box
          sx={{
            width: "38%",
            paddingLeft: "6px",
            display: "flex",
            borderRadius: "4px",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            sx={{ color: "#673C8E", fontSize: "16px", fontWeight: 400 }}
          >
            From
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD MMM YYYY"
              maxDate={filtrationData.to ? dayjs(filtrationData.to) : null}
              slotProps={{
                textField: {
                  placeholder: "DD MMM YYYY",
                },
              }}
              value={filtrationData.from ? dayjs(filtrationData.from) : null}
              onChange={(newValue) => handleDateChange("from", newValue)}
              PopoverProps={{ disableEnforceFocus: true }}
              sx={{
                "& .MuiInputBase-input": {
                  paddingRight: "4px",
                  padding: "7px",
                  fontSize: "14px",
                  color: "#673C8E",
                  borderRadius: "14px",
                  border: "none",
                },
                "& .css-1yq5fb3-MuiButtonBase-root-MuiIconButton-root": {
                  color: "#673C8E",
                },
                "& .MuiOutlinedInput-root fieldset": {
                  border: "2px solid rgba(103, 60, 142, 1)",
                },
                "& .MuiOutlinedInput-root fieldset:hover": {
                  borderColor: "#673C8E",
                },
                "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                  borderColor: "#673C8E",
                },
              }}
            />
          </LocalizationProvider>
        </Box>
        <Box
          sx={{
            width: "35%",
            paddingLeft: "6px",
            display: "flex",
            borderRadius: "4px",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography
            sx={{ color: "#673C8E", fontSize: "16px", fontWeight: 400 }}
          >
            To
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              format="DD MMM YYYY"
              minDate={filtrationData.from ? dayjs(filtrationData.from) : null}
              slotProps={{
                textField: {
                  placeholder: "DD MMM YYYY",
                },
              }}
              value={filtrationData.to ? dayjs(filtrationData.to) : null}
              onChange={(newValue) => handleDateChange("to", newValue)}
              PopoverProps={{ disableEnforceFocus: true }}
              sx={{
                "& .MuiInputBase-input": {
                  paddingRight: "4px",
                  fontSize: "14px",
                  padding: "7px",
                  color: "#673C8E",
                  borderRadius: "14px",
                  border: "none",
                },
                "& .css-1yq5fb3-MuiButtonBase-root-MuiIconButton-root": {
                  color: "#673C8E",
                },
                "& .MuiOutlinedInput-root fieldset": {
                  border: "2px solid rgba(103, 60, 142, 1)",
                },
                "& .MuiOutlinedInput-root fieldset:hover": {
                  borderColor: "#673C8E",
                },
                "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                  borderColor: "#673C8E",
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      </Grid>

      <Grid item xs={12} sx={{ paddingTop: "45px" }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontSize: "24px",
            color: "#673C8E",
            marginBottom: "20px",
          }}
        >
          User Count
        </Typography>
        <Grid container spacing={2}>
          <OrderCard title="Today" count={userCounts.today ?? 0} />
          <OrderCard title="This Week" count={userCounts.thisWeek ?? 0} />
          <OrderCard title="This Month" count={userCounts.thisMonth ?? 0} />
          <OrderCard title="This Year" count={userCounts.thisYear ?? 0} />
          <OrderCard title="Total Clients" count={userCounts.total ?? 0} />
        </Grid>
      </Grid>

      {/* ----------------------------- REGULAR ORDERS --------------------------------- */}
      <Grid item xs={12} sx={{ paddingTop: "45px" }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontSize: "24px",
            color: "#673C8E",
            marginBottom: "20px",
          }}
        >
          Regular Orders
        </Typography>
        <Grid container spacing={2}>
          <OrderCard title="Total Booked" count={stats.totalOrders} />
          <OrderCard title="Total Delivered" count={stats.deliveredOrders} />
          <OrderCard title="Total Cancelled" count={stats.cancelledOrders} />
          <OrderCard
            title="Total Revenue"
            count={Math.round(stats.totalRevenue).toLocaleString("en-IN")}
          />
          <OrderCard title="Total Drivers" count={stats.totalDrivers} />
        </Grid>
      </Grid>
      {/* ----------------------------- REGULAR ORDERS --------------------------------- */}

      {/* --------------------------- SPECIAL ORDERS ----------------------------- */}
      <Grid item xs={12} sx={{ paddingTop: "45px", marginBottom: "20px" }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            fontSize: "24px",
            color: "#673C8E",
            marginBottom: "20px",
          }}
        >
          VIP Orders
        </Typography>
        <Grid container spacing={2}>
          <OrderCard title="Total Booked" count={vipStats.totalOrders} />
          <OrderCard title="Total Delivered" count={vipStats.deliveredOrders} />
          <OrderCard title="Total Cancelled" count={vipStats.cancelledOrders} />
          <OrderCard
            title="Total Revenue"
            count={Math.round(vipStats.totalRevenue).toLocaleString("en-IN")}
          />
          <OrderCard title="Total Drivers" count={vipStats.totalDrivers} />
        </Grid>
      </Grid>
      {/* --------------------------- SPECIAL ORDERS ----------------------------- */}

      {/* -------------------------------- WEEKLY PERFORMANCE OF DRIVERS ---------------------------------------- */}
      <Grid xs={12} lg={6} item>
        <Card
          sx={{
            padding: "16px 20px",
            borderRadius: "14px",
            backgroundColor: "#ffffff",
            boxShadow: 5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "16px",
                color: "#673C8E", // Return the value directly
              }}
            >
              Weekly Active Drivers
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "29px",
                color: "#673C8E",
              }}
            >
              {overall}
            </Typography>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "10px",
                color: "rgba(104, 60, 140, 0.35)",
              }}
            >
              Excellent <br /> Performance
            </Typography>
          </Box>
          <Box sx={{ marginTop: "20px" }}>
            <DriversBarChart data={days} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: "11px",
                  color: "rgba(103, 60, 142, 1)", // Return the value directly
                }}
              >
                Completed Orders
              </Typography>
            </Box>
          </Box>
        </Card>
      </Grid>

      {/* -------------------------------- WEEKLY PERFORMANCE OF DRIVERS ---------------------------------------- */}

      {/* --------------------------------------- PIE CHART -------------------------------------- */}
      <Grid xs={12} lg={6} item>
        <Card
          sx={{
            padding: "16px 20px",
            borderRadius: "14px",
            boxShadow: 5,
            backgroundColor: "#ffffff",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "24px",
                color: "#673C8E", // Return the value directly
                // marginBottom: "20px",
              }}
            >
              Weekly Growth
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "20px",
              marginBottom: "16px",
            }}
          >
            <PieChart
              title="Customer Growth"
              apiEndpoint="/admin/weekly-growth"
            />
            <PieChart
              title="Drivers Growth"
              apiEndpoint="/admin/weekly-growth"
            />
            <PieChart
              title="Revenue Growth"
              apiEndpoint="/admin/weekly-growth"
            />
          </Box>
        </Card>
      </Grid>
      {/* --------------------------------------- PIE CHART -------------------------------------- */}
      {/* ---------------------------------------- TOTAL REVENUE --------------------------------------- */}
      <Grid xs={12} lg={6} item>
        <Card
          sx={{
            padding: "16px 20px",
            borderRadius: "14px",
            boxShadow: 5,
            backgroundColor: "#ffffff",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "20px",
                color: "#673C8E",
              }}
            >
              Total Revenue
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              {/* 2024 */}
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    backgroundColor: "rgba(104, 60, 140, 0.35)",
                  }}
                />
                <Typography variant="body2">2024</Typography>
              </Box>

              {/* 2025 */}
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    backgroundColor: "rgba(104, 60, 140, 1)",
                  }}
                />
                <Typography variant="body2">2025</Typography>
              </Box>
            </Box>

            {/* <RadioGroup
              row
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <FormControlLabel
                value="2024"
                control={
                  <Radio
                    sx={{
                      color: "rgba(104, 60, 140, 1)",
                      "&.Mui-checked": {
                        color: "rgba(104, 60, 140, 1)",
                      },
                    }}
                  />
                }
                label="2024"
              />
              <FormControlLabel
                value="2025"
                control={
                  <Radio
                    sx={{
                      color: "rgba(104, 60, 140, 1)",
                      "&.Mui-checked": {
                        color: "rgba(104, 60, 140, 1)",
                      },
                    }}
                  />
                }
                label="2025"
              />
            </RadioGroup> */}
          </Box>
          <Box sx={{ overflowX: "scroll", overflowY: "hidden" }}>
            {loading ? (
              <Typography>Loading...</Typography>
            ) : error ? (
              <Typography color="error">{error}</Typography>
            ) : (
              <>
                <TotalRevenueLineChart
                  data={chartData}
                  selectedYear={selectedYear}
                />
              </>
            )}
          </Box>
        </Card>
      </Grid>
      {/* ---------------------------------------- TOTAL REVENUE --------------------------------------- */}

      {/* ------------------------------------------ CUSTOMER MAP ---------------------------------------- */}
      <Grid xs={12} lg={6} item>
        <Card
          sx={{
            padding: "16px 20px",
            borderRadius: "14px",
            boxShadow: 5,
            backgroundColor: "#ffffff",
          }}
        >
          <Box
            sx={{
              height: "80px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "20px",
                color: "#673C8E", // Return the value directly
                // marginBottom: "20px",
              }}
            >
              Customer Orders Map
            </Typography>
          </Box>
          <Box>
            <CustomerBarChart />
          </Box>
        </Card>
      </Grid>
      {/* ------------------------------------------ CUSTOMER MAP ---------------------------------------- */}

      <Grid xs={12} lg={4.25} item>
        <Card
          sx={{
            boxShadow: 5,
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            pt: 3,
            px: 1,
            height: "410px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              p: 2,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: "24px", color: "#673C8E" }}
            >
              Active Drivers
            </Typography>
            {drivers.length > 0 && (
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: "12px",
                  color: "#673C8E",
                  cursor: "pointer",
                }}
                onClick={handleSeeAll}
              >
                {showAll ? "See Less" : "See All"}
              </Typography>
            )}
          </Box>

          {drivers.length === 0 && (
            <Typography sx={{ textAlign: "center", py: 4, color: "#BDC1CA" }}>
              No active drivers found
            </Typography>
          )}

          {displayedDrivers.map((driver) => (
            <Box
              key={driver.id}
              sx={{
                padding: "20px 40px",
                borderBottom: "5px solid rgba(0, 0, 0, 0.06)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Avatar
                sx={{ width: 50, height: 50 }}
                alt={driver.name}
                src={driver.profileImage || "/static/images/avatar/1.jpg"}
              />
              <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
                <Typography
                  sx={{ fontWeight: 400, fontSize: "16px", color: "#171A1F" }}
                >
                  {driver.name}
                </Typography>
              </Box>
              <Tooltip title={driver.phone} arrow>
                <IconButton onClick={() => handleClick(driver.callLink)}>
                  <img
                    src={phone}
                    alt="Call"
                    style={{ width: 24, height: 24 }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Card>
      </Grid>

      <Grid xs={12} lg={4.25} item>
        <Card
          sx={{
            boxShadow: 5,
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            pt: 3,
            px: 1,
            height: "410px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              maxHeight: "300px",
              p: 2,
            }}
          >
            <Typography
              sx={{ fontWeight: 700, fontSize: "24px", color: "#673C8E" }}
            >
              Latest Orders
            </Typography>
            {latestOrders.length > 0 && (
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: "12px",
                  color: "#673C8E",
                  cursor: "pointer",
                }}
                onClick={handleSeeAllOrders}
              >
                {showAllOrders ? "See Less" : "See All"}
              </Typography>
            )}
          </Box>

          {latestOrders.length === 0 && (
            <Typography sx={{ textAlign: "center", py: 4, color: "#BDC1CA" }}>
              No orders found
            </Typography>
          )}

          {ordersToDisplay.map((order) => (
            <Box
              key={order.orderId}
              sx={{
                padding: "20px 40px",
                borderBottom: "5px solid rgba(0, 0, 0, 0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderRadius: "8px",
              }}
            >
              {/* Avatar */}
              <Avatar
                sx={{ width: 50, height: 50 }}
                alt={order.orderId}
                src={order.image || "/static/images/avatar/1.jpg"}
              />

              {/* Order Info */}
              <Box sx={{ flex: 1, marginLeft: "12px" }}>
                <Typography
                  sx={{ fontWeight: 500, fontSize: "14px", color: "#171A1F" }}
                >
                  {order.orderId} | {order.address}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "13px",
                    color: "#171A1F",
                    mt: 0.5,
                  }}
                >
                  {dayjs(new Date(order.pickupTime)).format("h:mm A")} to{" "}
                  {dayjs(new Date(order.dropOffTime)).format("h:mm A")}
                </Typography>
              </Box>

              {/* Pickup Count */}
              <Box
                sx={{
                  textAlign: "right",
                  marginLeft: "12px",
                  marginTop: "40px",
                }}
              >
                <Typography
                  sx={{ fontWeight: 400, fontSize: "12px", color: "#171A1F" }}
                >
                  {order.pickupCount} pickups
                </Typography>
              </Box>
            </Box>
          ))}
        </Card>
      </Grid>

      <Grid xs={12} lg={3.5} item>
        <Card
          sx={{
            boxShadow: 5,
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            pt: 3,
            px: 2,
            display: "flex",
            flexDirection: "column",
            // height: "85%", // fill grid height if needed
            height: "410px",
            pb: 2, // 👈 bottom padding
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                sx={{ fontWeight: 600, fontSize: "20px", color: "#673C8E" }}
              >
                Calendar
              </Typography>
              <Typography
                sx={{ fontSize: "14px", fontWeight: 500, color: "#673C8E" }}
              >
                {todayOrderCount} Orders today
              </Typography>
            </Box>
            <IconButton>
              {/* <CalendarTodayIcon sx={{ color: "#673C8E" }} /> */}
              <img src={calendar} alt="Calendar Image" />
            </IconButton>
          </Box>

          {/* Today Section */}
          <Box sx={{ mt: 2 }}>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: "#673C8E",
                mb: 1,
              }}
            >
              Today
            </Typography>

            <Box
              sx={{
                maxHeight: 90,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1, // cleaner than mb in children
              }}
            >
              {todayOrders.map((order, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    borderRadius: "8px",
                    backgroundColor: "#673C8E",
                    color: "#fff",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {/* <EventIcon /> */}
                    <img src={calendarWhite} alt="Calendar White Image" />
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                        {order.title}
                      </Typography>
                      <Typography sx={{ fontSize: "12px" }}>
                        {dayjs(new Date(order.pickupTime)).format("h:mm A")} to{" "}
                        {dayjs(new Date(order.dropOffTime)).format("h:mm A")}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: "12px" }}>
                    {order.distance}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Next 7 Days Section */}
          <Box sx={{ mt: 2 }}>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                color: "#673C8E",
                mb: 1,
              }}
            >
              Next 7 days
            </Typography>

            <Box
              sx={{
                maxHeight: "140px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {next7DaysOrders.map((order, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    borderRadius: "8px",
                    backgroundColor: "rgba(103, 60, 142, 0.2)",
                    color: "#171A1F",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {/* <EventIcon sx={{ color: "#673C8E" }} /> */}
                    <img src={calendarColor} alt="Calendar Color Image" />
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                        {order.title}
                      </Typography>
                      <Typography sx={{ fontSize: "12px" }}>
                        {dayjs(new Date(order.pickupTime)).format("h:mm A")} to{" "}
                        {dayjs(new Date(order.dropOffTime)).format("h:mm A")}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: "12px", color: "#673C8E" }}>
                    {order.distance}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Card>
      </Grid>

      {/* -------------------------------------- REVIEW CAROUSEL ---------------------------------------- */}
      <Grid item xs={12} lg={12}>
        <Box
          sx={{
            position: "relative",
            paddingTop: "40px",
            boxShadow: "0px 4px 6px -2px rgba(0, 0, 0, 0.1)",
            gap: 1,
          }}
        >
          <ReviewCarousel />
        </Box>
      </Grid>
      {/* -------------------------------------- REVIEW CAROUSEL ---------------------------------------- */}
    </Grid>
  );
}
